import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, X, Check } from "@phosphor-icons/react";

/**
 * Combobox multi-selección con búsqueda por teclado.
 *
 * Props:
 * - value: string[]  → ids seleccionados ([] = ninguno)
 * - onChange: (ids: string[]) => void
 * - options: { value: string, label: string, hint?: string }[]
 * - placeholder: string
 * - disabled: bool
 * - className: string extra para el trigger
 * - "data-testid": string
 */
export default function MultiSearchableSelect({
    value = [],
    onChange,
    options,
    placeholder = "Todos",
    disabled = false,
    className = "",
    ...rest
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const testid = rest["data-testid"];

    const selectedSet = useMemo(() => new Set(value), [value]);

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.trim().toLowerCase();
        return options.filter((o) => {
            const lab = String(o.label || "").toLowerCase();
            const hint = String(o.hint || "").toLowerCase();
            return lab.includes(q) || hint.includes(q);
        });
    }, [options, query]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 10);
        else setQuery("");
    }, [open]);

    const toggle = (v) => {
        if (selectedSet.has(v)) onChange(value.filter((x) => x !== v));
        else onChange([...value, v]);
    };

    const clear = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    const summary = useMemo(() => {
        if (!value.length) return placeholder;
        if (value.length === 1) {
            const o = options.find((x) => x.value === value[0]);
            return o ? o.label : "1 seleccionado";
        }
        return `${value.length} proyectos seleccionados`;
    }, [value, options, placeholder]);

    return (
        <div ref={wrapRef} className="relative" data-testid={testid}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                className={`w-full flex items-center justify-between border border-slate-300 rounded-sm px-3 py-2 bg-white text-sm text-left hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
                data-testid={testid ? `${testid}-trigger` : "multiselect-trigger"}
            >
                <span className={value.length ? "truncate text-slate-900" : "text-slate-400"}>
                    {summary}
                </span>
                <span className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {value.length > 0 && !disabled && (
                        <span
                            role="button"
                            tabIndex={-1}
                            onClick={clear}
                            className="text-slate-400 hover:text-slate-600 p-0.5"
                            data-testid={testid ? `${testid}-clear` : "multiselect-clear"}
                        >
                            <X size={12} />
                        </span>
                    )}
                    <CaretDown size={12} className="text-slate-400" />
                </span>
            </button>
            {open && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-hidden flex flex-col">
                    <div className="relative border-b border-slate-100">
                        <MagnifyingGlass
                            size={12}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Escribe para buscar..."
                            className="w-full pl-7 pr-2 py-2 text-sm outline-none"
                            data-testid={testid ? `${testid}-search` : "multiselect-search"}
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {filtered.length === 0 ? (
                            <div className="text-center py-6 text-xs text-slate-400">
                                Sin resultados
                            </div>
                        ) : (
                            filtered.slice(0, 200).map((o) => {
                                const active = selectedSet.has(o.value);
                                return (
                                    <button
                                        type="button"
                                        key={o.value}
                                        onClick={() => toggle(o.value)}
                                        className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                                            active
                                                ? "bg-[#002BAF]/5 text-[#002BAF] font-medium"
                                                : "text-slate-700"
                                        }`}
                                        data-testid={
                                            testid ? `${testid}-option-${o.value}` : undefined
                                        }
                                    >
                                        <span
                                            className={`w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                                                active
                                                    ? "bg-[#002BAF] border-[#002BAF] text-white"
                                                    : "border-slate-300"
                                            }`}
                                        >
                                            {active && <Check size={11} weight="bold" />}
                                        </span>
                                        <span className="truncate flex-1">{o.label}</span>
                                        {o.hint && (
                                            <span className="text-[10px] text-slate-400 font-mono ml-2 flex-shrink-0">
                                                {o.hint}
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                        {filtered.length > 200 && (
                            <div className="text-center py-2 text-[10px] text-slate-400 border-t border-slate-100">
                                Mostrando 200 de {filtered.length}. Refina la búsqueda.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
